import { Injectable } from '@angular/core';
import { LoginService } from '../login/store/login.service';
import { select, Store } from '@ngrx/store';
import * as LoginSelectors from '../login/store/login.selectors';
import { ClpInitConfig } from '@reef/login';

@Injectable({
  providedIn: 'root'
})
export class SsoService {

  private clpConfig?: ClpInitConfig;

  private readonly SSO_CALLBACK = '/sso/callback';
  private readonly SSO_WITH_SLASHES = '/sso/';
  private readonly APP_STORE_CLIENT_ID = "1ASIXSCOAPSTOR";
  private readonly APP_STORE_DOMAIN = `https://${window.location.hostname}`;
  private readonly APP_STORE_URI_CALLBACK = "/appstore/sso/callback";
  private readonly APP_STORE_SSO_RESPONSE_TYPE = 'id_token+token';
  private readonly APP_STORE_SSO_PROMPT = 'none';
  private readonly APP_STORE_SSO_SCOPE = 'openid';
  private readonly SSO_NONCE_STORAGE_KEY = "sso-nonce";
  private readonly CLP_PATTERN: RegExp =
      /^https:\/\/([a-zA-Z0-9.]+\.)?accounts\.(sellingplatformconnect\.)?amadeus\.com$/;
  private readonly CLP_URL_QUERY_PARAM = "clpUrl";
  private readonly ACCESS_TOKEN_FRAGMENT = "access_token";
  private readonly ID_TOKEN_FRAGMENT = "id_token";

  constructor(private readonly loginService: LoginService,
              private readonly store: Store<any>,
  ) {
    this.store.pipe(select(LoginSelectors.selectClpConfig)).subscribe(clpConfig => {
      this.clpConfig = clpConfig;
    });
  }

  /**
   * Perform SSO login by extracting the access token and id token from the URL after CLP redirection
   */
  loginBySSO(): void {
    const params = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = params.get(this.ACCESS_TOKEN_FRAGMENT) ?? '';
    const idToken = params.get(this.ID_TOKEN_FRAGMENT) ?? '';
    const nonce = localStorage.getItem(this.SSO_NONCE_STORAGE_KEY) ?? '';
    this.loginService.loginByAccessToken({ accessToken, idToken, nonce });
  }

  /**
   * Check whether user is connected through SSO
   */
  isSSOLoginFlow(): boolean {
    return window.location.pathname.includes(this.SSO_CALLBACK);
  }

  /**
   * Check whether the path relates to SSO
   */
  isSSOInPath(): boolean {
    return window.location.pathname.includes(this.SSO_WITH_SLASHES);
  }

  /**
   * Trigger SSO flow calling CLP, the prompt parameter is set to 'none' to avoid the login prompt
   */
  triggerClpSSO() {
    let clpUrl = this.retrieveAndWhitelistClpUrl();
    if (!clpUrl) {
      clpUrl = this.clpConfig?.baseUrl;
    }

    window.location.href = `${clpUrl}/LoginService/authorize?` +
        `client_id=${this.APP_STORE_CLIENT_ID}` +
        `&redirect_uri=${this.APP_STORE_DOMAIN}${this.APP_STORE_URI_CALLBACK}` +
        `&response_type=${this.APP_STORE_SSO_RESPONSE_TYPE}` +
        `&scope=${this.APP_STORE_SSO_SCOPE}` +
        `&nonce=${this.generateNonce()}` +
        `&prompt=${this.APP_STORE_SSO_PROMPT}`;
  }

  /**
   * Generate a random nonce of 6 characters and store it in the local storage
   */
  private generateNonce(): string {
    const nonce = this.clpConfig?.nonce ?? '';
    localStorage.setItem(this.SSO_NONCE_STORAGE_KEY, nonce);
    return nonce;
  }

  /**
   * Retrieve the CLP URL from the query parameters
   */
  private retrieveAndWhitelistClpUrl(): string | undefined {
    const urlParams = new URLSearchParams(window.location.search);
    const clpUrl = urlParams.get(this.CLP_URL_QUERY_PARAM) ?? "";
    return this.CLP_PATTERN.test(clpUrl) ? clpUrl : undefined;
  }
}
